import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";

function HeroVideoSlider({ data }: any) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isReady, setIsReady] = useState(false);

  const videoSrc = data?.video;
  const posterSrc = data?.banner || data?.thumbnail;
  const tag = data?.tag || "AYOZAT";
  const title = data?.title || "Everything You Love. One Platform.";
  const description =
    data?.description ||
    "Movies, series, music, podcasts, live TV and more — all in one place.";

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoSrc) return;

    // Speed up the connection handshake to the CDN before we even request data
    let preconnect: HTMLLinkElement | null = null;
    try {
      const origin = new URL(videoSrc).origin;
      preconnect = document.createElement("link");
      preconnect.rel = "preconnect";
      preconnect.href = origin;
      preconnect.crossOrigin = "anonymous";
      document.head.appendChild(preconnect);
    } catch {
      // ignore malformed URL
    }

    const handleReady = () => setIsReady(true);
    video.addEventListener("loadeddata", handleReady);

    let hls: Hls | null = null;

    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      // Safari / iOS support HLS natively
      video.src = videoSrc;
    } else if (Hls.isSupported()) {
      hls = new Hls({
        // start on the lowest quality so playback can begin as fast as
        // possible, then let ABR step up once there's enough bandwidth
        startLevel: 0,
        capLevelToPlayerSize: true,
        maxBufferLength: 15,
        backBufferLength: 30,
        enableWorker: true,
      });
      hls.on(Hls.Events.ERROR, (_event, errData) => {
        // eslint-disable-next-line no-console
        console.error("HeroVideoSlider HLS error:", errData);
        if (errData.fatal) {
          switch (errData.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              hls?.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              hls?.recoverMediaError();
              break;
            default:
              hls?.destroy();
              break;
          }
        }
      });

      hls.loadSource(videoSrc);
      hls.attachMedia(video);
    }

    const handleVideoError = () => {
      // eslint-disable-next-line no-console
      console.error("HeroVideoSlider <video> element error:", video.error);
    };
    video.addEventListener("error", handleVideoError);

    return () => {
      video.removeEventListener("loadeddata", handleReady);
      video.removeEventListener("error", handleVideoError);
      hls?.destroy();
      preconnect?.remove();
    };
  }, [videoSrc]);

  if (!videoSrc) return null;

  return (
    <div className="hero-slider relative mt-4 mb-10">
      <div className="hero-slide relative flex h-[68vh] w-full overflow-hidden rounded-2xl max-sm:h-[60vh]">
        {/* scoped animations */}
        <style>{`
          @keyframes heroFadeUp {
            from { opacity: 0; transform: translateY(24px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .hero-video-content > * {
            opacity: 0;
            animation: heroFadeUp 0.7s ease-out forwards;
          }
          .hero-video-content > *:nth-child(1) { animation-delay: 0.05s; }
          .hero-video-content > *:nth-child(2) { animation-delay: 0.15s; }
          .hero-video-content > *:nth-child(3) { animation-delay: 0.25s; }
          @media (prefers-reduced-motion: reduce) {
            .hero-video-content > * { animation: none; opacity: 1; }
          }
          .hero-cta-btn {
            position: relative;
          }
          .hero-cta-btn::after {
            content: "";
            position: absolute;
            inset: 0;
            border-radius: 9999px;
            border: 2px solid #F50537;
            opacity: 0;
            pointer-events: none;
          }
          .hero-cta-btn:hover::after {
            animation: heroCtaPulse 1.1s ease-out infinite;
          }
          @keyframes heroCtaPulse {
            0% { opacity: 0.7; transform: scale(1); }
            100% { opacity: 0; transform: scale(1.35); }
          }
          @media (prefers-reduced-motion: reduce) {
            .hero-cta-btn:hover::after { animation: none; }
          }
        `}</style>

        {/* Poster shown instantly while the stream buffers */}
        {posterSrc && (
          <img
            src={posterSrc}
            alt=""
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${
              isReady ? "opacity-0" : "opacity-100"
            }`}
          />
        )}

        {/* Background video */}
        <video
          ref={videoRef}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${
            isReady ? "opacity-100" : "opacity-0"
          }`}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
        />

        {/* Content — lower-third left */}
        <div className="relative z-10 flex h-full w-full items-end">
          <div className="hero-video-content flex w-full max-w-[640px] flex-col items-start gap-4 p-12 max-sm:max-w-full max-sm:gap-3 max-sm:p-5">
            {/* Eyebrow / caption */}
            <p className="inline-flex items-center rounded-full bg-[#F50537] px-3.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white shadow-lg shadow-[#F50537]/30">
              {tag}
            </p>

            {/* Title */}
            <h2 className="font-rubik text-[52px] font-extrabold leading-[1.02] tracking-tight text-white drop-shadow-2xl max-sm:text-[28px]">
              {title}
            </h2>

            {/* Synopsis */}
            <p className="line-clamp-2 max-w-xl text-[15px] leading-relaxed text-white/80 max-sm:text-[13px]">
              {description}
            </p>

            {/* Optional CTA */}
            {data?.button_label && data?.button_url ? (
              <a
                href={data.button_url}
                // target="_blank"
                rel="noreferrer"
                className="hero-cta-btn mt-2 flex h-[48px] w-[180px] items-center justify-center rounded-full bg-[#F50537] text-center font-semibold text-white shadow-lg shadow-[#F50537]/30 transition-all duration-300 hover:-translate-y-0.5 hover:scale-[1.03] hover:brightness-110 max-sm:w-[160px]"
              >
                {data.button_label}
              </a>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default HeroVideoSlider;
